import { Component, OnInit } from '@angular/core';
import { Message, ConfirmationService } from 'primeng';
import { HttpClient } from '@angular/common/http';
import { BillingService, EnvironmentService, SystemDataService } from '../../services';
import { SettingsService } from '../../services';
import { AppConfig, SupportConfig, MaintenanceSettings, MaintenanceOptions } from './settings';
import { BBWTMessageService } from '../../services';
import { SystemDateTimeInfo } from './settings';
import moment from 'moment';
import { Version } from '../../interfaces/version';

@Component({
  selector: 'settings',
  templateUrl: './settings.component.html',
  providers: [],
})
export class SettingsComponent implements OnInit {
  Settings: AppConfig;
  msgs: Message[] = [];
  revolutSettingsUrl = '/authorization/GetRevolutAuthorizationCode';
  currentDate: Date = new Date();
  showLoader: boolean = false;
  revolutAccessKeyModifyMsg = {
    onRequestAuthorizationKey:
      'Are you sure you want to request a new authorisation code? This will reset any existing authorisation code currently in use',
    onRemoveAccessKeys: 'Are you sure you want to remove all access keys?',
  };
  public systemDateTimeInfo: SystemDateTimeInfo;

  public versionInformation: Version;

  constructor(
    private http: HttpClient,
    public messageService: BBWTMessageService,
    private billingService: BillingService,
    private settingsService: SettingsService,
    private confirmationService: ConfirmationService,
    private environmentService: EnvironmentService,
    private systemDataService: SystemDataService,
  ) {}

  ngOnInit() {
    this.Settings = new AppConfig();
    this.Settings.maintenanceSettings = new MaintenanceSettings();
    this.Settings.support = new SupportConfig();
    this.LoadSettings();
    this.getCurrentSystemDateTimeInformation();
    this.getCurrentVersionInformation();
  }

  OnChangeMaintenanceStart(): void {
    // https://github.com/primefaces/primeng/issues/4755
    // manual enddate updating
    if (this.Settings.maintenanceSettings.start > this.Settings.maintenanceSettings.end) {
      this.Settings.maintenanceSettings.end = this.Settings.maintenanceSettings.start;
    }
  }

  LoadSettings(): void {
    this.http
      .get<AppConfig>('/api/settings/')
      .toPromise()
      .then((d) => {
        this.Settings = this.parseSettingsResponse(d);
        this.ShowSuccess('success', 'Success', 'Configuration reset.');
        console.log('Reset settings success');
      })
      .catch((e) => {
        this.ShowSuccess('error', 'Error', 'Failed to reset configuration.');
        console.log('Reset settings error');
      });
  }

  Save(): void {
    if (this.checkMaintenanceSettings()) {
      this.http
        .post('/api/settings/', this.Settings)
        .toPromise()
        .then((d) => {
          this.ShowSuccess('success', 'Success', 'Configuration saved.');
          console.log('Save settings success');
        })
        .catch((e) => {
          this.ShowSuccess('error', 'Error', 'Failed to save configuration.');
          console.log('Save settings error');
        });
    } else {
      this.msgs = [];
      this.msgs.push({
        severity: 'error',
        summary: 'System Configuration',
        detail: 'Please set the  correct start and end dates of Maintenance Settings.',
      });
    }
  }

  Reset(): void {
    this.LoadSettings();
  }

  ShowSuccess(type, summary, message) {
    this.msgs = [];
    this.msgs.push({ severity: type, summary: summary, detail: message });
  }

  private parseSettingsResponse(result: AppConfig): AppConfig {
    var maintenanceSettings = result.maintenanceSettings;
    maintenanceSettings.start = !maintenanceSettings.start
      ? moment().startOf('day').toDate()
      : moment(maintenanceSettings.start).toDate();
    maintenanceSettings.end = !maintenanceSettings.end
      ? moment().startOf('day').toDate()
      : moment(maintenanceSettings.end).toDate();
    return result;
  }

  private checkMaintenanceSettings(): boolean {
    if (
      this.Settings.maintenanceSettings.isActive &&
      this.Settings.maintenanceSettings.option == MaintenanceOptions.Basic
    ) {
      var startDate = this.Settings.maintenanceSettings.start;
      var endDate = this.Settings.maintenanceSettings.end;

      if (startDate < this.currentDate || startDate > endDate || endDate < startDate) {
        return false;
      }
    }

    return true;
  }

  setRevolutWebhook() {
    this.billingService
      .setupRevolutWebhookUrl()
      .then((res) => {
        if (res) this.ShowSuccess('success', 'Success', 'Revolut webhook is set up.');
        else this.ShowSuccess('error', 'Error', 'Failed to set up Revolut webhook');
      })
      .catch(() => {
        this.ShowSuccess('error', 'Error', 'Failed to set up Revolut webhook');
      });
  }

  openRevolutAuthorizationUrl() {
    this.confirmRevolutAccessKeysAction(
      this.revolutAccessKeyModifyMsg.onRequestAuthorizationKey,
      () => {
        window.open(this.revolutSettingsUrl);
      },
    );
  }

  resetRevolutAccessCodes() {
    this.confirmRevolutAccessKeysAction(this.revolutAccessKeyModifyMsg.onRemoveAccessKeys, () => {
      this.settingsService.removeRevolutAccessKeys().then((result) => {
        if (result) {
          location.reload();
        } else {
          this.messageService.ShowError(
            `Error resetting Revolut access keys`,
            'Reset Revolut Access Keys',
            5,
          );
        }
      });
    });
  }

  confirmRevolutAccessKeysAction(message: string, callbackFunction: Function) {
    this.confirmationService.confirm({
      message: message,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      key: 'modify-revolut-access-keys-dialog',
      accept: () => {
        if (callbackFunction) callbackFunction();
      },
      reject: () => {},
    });
  }

  getCurrentSystemDateTimeInformation() {
    this.settingsService
      .getCurrentSystemDateTimeInfo()
      .then((result) => {
        this.systemDateTimeInfo = result;
      })
      .catch((err) => console.log('Error retrieving system date time info'));
  }

  getCurrentVersionInformation(): void {
    this.systemDataService.getVersionInfo().then((version) => {
      if (version) {
        this.versionInformation = version;
      }
    });
  }
}
